import React, { useEffect, useState } from 'react';
import DueTimeModeTypeOption, { DueTimeModeType } from '@/types/DueTimeType';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import { RouteMapExecutionService } from '@services/actions/RouteMapExecutionService';
import { getABookFormValues } from '@utils/abookUtils';
import TransitionTypeOption from '@/types/TransitionType';
import {
    IExtraStepSettingsResult,
    IRouteMapActionResult,
} from '@models/document/routeMapActionsExecutors/IRouteMapActionModel';
import DurationSpanTypeOption from '@/types/DurationSpanType';
import { useForm } from 'react-hook-form';
import { IOption } from '@/types';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import { IAddRouteMapProps } from './IAddRouteMapProps';
import { IAddRouteMapFormData } from './IAddRouteMapFormData';
import TextareaControl from '@controls/TextareaControl';
import AbookpickerControl from '@controls/AbookpickerControl';
import SelectControl from '@controls/SelectControl';
import InputNumberControl from '@controls/InputNumberControl';
import DatepickerControl from '@controls/DatepickerControl';

export const AddRouteMapModal: React.FC<IAddRouteMapProps> = (props: IAddRouteMapProps) => {
    const [errorText, setErrorText] = useState<string>();
    const [loading, setLoading] = useState<boolean>(true);
    const [periodType, setPeriodType] = useState<DueTimeModeType>(DueTimeModeType[props.defaultDueTime.type]);
    const [executors, setExecutors] = useState<IAddressBookData[]>();
    const [formValues, setFormValues] = useState<string>('{}');
    const activated = React.useRef(false);

    const [restrictedTransitionType, setRestrictedTransitionType] = useState<boolean>();
    const [defaultTransitionType, setDefaultTransitionType] = useState<IOption[]>([TransitionTypeOption[0]]);

    const serv = new RouteMapExecutionService();

    const getFormValues = async () => {
        const vals = await getABookFormValues(props.documentKey, props.abookOptions?.formValues);
        if (activated.current && vals) {
            setFormValues(vals);
        }
    };

    useEffect(() => {
        activated.current = true;
        setLoading(true);
        serv.getExtraStepSettings(props.processKey).then((res) => {
            getFormValues();
            setStageSettings(res.data);
            setLoading(false);
        });
        return () => {
            activated.current = false;
        };
    }, []);

    const setStageSettings = (data: IExtraStepSettingsResult) => {
        if (
            data.restrictedTransitionType != undefined &&
            data.restrictedTransitionType &&
            data.restrictedStepNames != undefined &&
            data.restrictedStepNames.length > 0
        ) {
            const currentStep = props.rowData?.fields['Name'];
            if (currentStep != undefined && data.restrictedStepNames.indexOf(currentStep) > -1) {
                setRestrictedTransitionType(true);
                setDefaultTransitionType([TransitionTypeOption[1]]);
                formState.reset({ ...formState.getValues(), transitionType: [TransitionTypeOption[1]] });
            }
        }
    };

    const onSubmit = (form: IAddRouteMapFormData) => {
        if (!form?.executors) {
            setErrorText('Не указаны исполнители');
            return;
        }

        if (!form?.tasktext) {
            setErrorText('Не указан текст задачи');
            return;
        }

        setLoading(true);

        const transitionType = (form.transitionType[0] ?? TransitionTypeOption[0]).value;

        const result = {
            levelKey: props.levelKey,
            nextlevelKey: props.nextlevelKey,
            stepName: form.stepName,
            executorsIds: form.executors.split('|').map((n) => parseInt(n, 10)),
            dueTimeMode: DueTimeModeType[periodType],
            transitionType: transitionType,
            tasktext: form.tasktext,
        } as IRouteMapActionResult;

        if (periodType == DueTimeModeType.Date) {
            if (!form?.periodDate) {
                setErrorText('Не указан срок исполнения');
                return;
            }

            result.periodDate = form.periodDate;
        }

        if (periodType == DueTimeModeType.TimeSpan) {
            result.termType = (form.termType[0] ?? DurationSpanTypeOption[0]).value;
            result.termCount = form.termCount ?? 1;
        }

        return serv
            .add(props.actionKey, props.activityKey, props.processKey, props.documentKey, result)
            .then((res) => {
                setLoading(false);

                props.completeHandler && props.completeHandler(true);
            })
            .catch((err) => {
                setErrorText(err);
            })
            .finally(() => setLoading(false));
    };

    const formState = useForm<IAddRouteMapFormData>({
        mode: 'onBlur', // "onChange"
    });

    const onPeriodTypeChange = (option: IOption[]) => {
        if (option.length == 0) return;
        let type = DueTimeModeType[option[0].value as keyof typeof DueTimeModeType];
        setPeriodType(type);
    };

    const modalContainerProps: IModalContainerProps = {
        header: 'Создание этапа',
        size: props.modalSize,
        showOkButton: true,
        showCancelButton: true,
        cancelClick: () => {
            props.completeHandler && props.completeHandler(false);
        },
        errorText: errorText,
        formMethods: formState,
        onSubmit: onSubmit,
        okButtonDisabled: false,
    };

    return (
        <ModalContainer {...modalContainerProps}>
            <TextareaControl
                label="Название этапа"
                initialRowCount={1}
                readOnly={true}
                required={true}
                defaultValue={'Дополнительное согласование'}
                rules={{ required: 'Обязательное поле' }}
                name="stepName"
                formState={formState.formState}
                control={formState.control}
                onValueChange={(val: any) => {}}
            />

            <AbookpickerControl
                label="Исполнители"
                placeholder="Выберите исполнителей"
                isMultiple={true}
                title="Исполнители"
                displayValue={executors?.map((x) => x.fullName)?.join(',') ?? ''}
                formValues={formValues}
                name="executors"
                tabsSettings={props.abookOptions?.tabs}
                externalSearch={props.abookOptions?.externalSearch}
                required={true}
                rules={{ required: 'Обязательное поле' }}
                formState={formState.formState}
                showChips={false}
                control={formState.control}
                onSetValues={(value: IAddressBookData[]) => {
                    setExecutors(value);
                }}
            />

            <SelectControl
                name="periodType"
                label="Срок исполнения"
                multiselect={false}
                readOnly
                placeholder="Укажите срок исполнения"
                required={true}
                rules={{ required: 'Обязательное поле' }}
                options={DueTimeModeTypeOption}
                preloader={loading}
                formState={formState.formState}
                control={formState.control}
                defaultValue={[DueTimeModeTypeOption.find((option) => option.value === props.defaultDueTime.type)]}
                onChangeVal={onPeriodTypeChange}
            />

            {periodType === DueTimeModeType.TimeSpan && (
                <>
                    <InputNumberControl
                        name="termCount"
                        defaultValue={props.defaultDueTime.timeSpanValue ?? 1}
                        min={1}
                        max={9999}
                        floatPoints={0}
                        required={true}
                        rules={{ required: 'Обязательное поле' }}
                        formState={formState.formState}
                        control={formState.control}
                    />

                    <SelectControl
                        multiselect={false}
                        name="termType"
                        readOnly
                        required={true}
                        rules={{ required: 'Обязательное поле' }}
                        options={DurationSpanTypeOption}
                        preloader={loading}
                        formState={formState.formState}
                        control={formState.control}
                        defaultValue={[
                            DurationSpanTypeOption.find(
                                (option) => option.value === props.defaultDueTime.timeSpanType,
                            ) ?? DurationSpanTypeOption[2],
                        ]}
                    />
                </>
            )}

            {periodType === DueTimeModeType.Date && (
                <DatepickerControl
                    name="periodDate"
                    required={true}
                    rules={{ required: 'Обязательное поле' }}
                    formState={formState.formState}
                    control={formState.control}
                />
            )}

            <SelectControl
                label="Тип этапа"
                multiselect={false}
                readOnly
                required={true}
                rules={{ required: 'Обязательное поле' }}
                name="transitionType"
                // если этап добавляется в пустую карту маршрута, то доступен только тип этапа Последующий
                options={
                    restrictedTransitionType
                        ? [TransitionTypeOption[1]]
                        : props.activityKey
                          ? TransitionTypeOption
                          : [TransitionTypeOption[0]]
                }
                preloader={loading}
                formState={formState.formState}
                control={formState.control}
                defaultValue={defaultTransitionType}
            />

            <TextareaControl
                label="Текст задачи"
                readOnly={false}
                required={true}
                rules={{ required: 'Обязательное поле' }}
                name="tasktext"
                defaultValue={props.prefilledComment}
                formState={formState.formState}
                control={formState.control}
                onValueChange={(val: any) => {}}
            />
        </ModalContainer>
    );
};
